import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Field } from "./Fleet";
import { Plus, X, Trash2, Pencil, Upload, Download } from "lucide-react";
import { validateRut, formatRut } from "@/lib/rut";

const EMPTY = { name: "", rut: "", address: "", phone: "", email: "" };

export default function Companies() {
  const { user } = useAuth();
  const [tab, setTab] = useState("clients");

  const tabs = [
    { k: "clients", label: "Empresas Cliente" },
    { k: "workshops", label: "Directorio Talleres" },
  ];

  return (
    <div data-testid="companies-page">
      <PageHeader
        tag="Directorio"
        title="Empresas Asociadas"
        description="Gestiona tus empresas cliente y el directorio de talleres con los que trabajas. También puedes cargarlas en bloque desde Excel."
      />

      <div className="border-b border-gray-200 flex gap-1 mb-8 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`companies-tab-${t.k}`}
            className={`px-4 md:px-6 py-3 text-xs font-bold uppercase tracking-wider border-b-4 transition-colors ${
              tab === t.k ? "border-blue-600 text-black" : "border-transparent text-gray-500 hover:text-black"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "clients" && <ClientsTab />}
      {tab === "workshops" && <WorkshopsTab />}
    </div>
  );
}

function ClientsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/client-companies").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Section
      title="Mis empresas cliente"
      description="Empresas a las que arriendas vehículos."
      items={items}
      newLabel="Nueva empresa cliente"
      onNew={() => setEditing({ ...EMPTY, _new: true })}
      onEdit={(c) => setEditing({ ...c })}
      onDelete={async (c) => {
        if (!window.confirm(`¿Eliminar a "${c.name}"?`)) return;
        try { await api.delete(`/clients/${c.id}`); load(); }
        catch (e) { alert(e.response?.data?.detail || "Error"); }
      }}
      importType="clients"
      onImported={load}
    >
      {editing && <CompanyModal value={editing} type="client" onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </Section>
  );
}

function WorkshopsTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/workshops").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Section
      title="Directorio de talleres"
      description="Talleres con los que trabajas. Si el taller se registra después con el mismo correo, podrás vincular esta entrada."
      items={items}
      newLabel="Nuevo taller"
      onNew={() => setEditing({ ...EMPTY, _new: true })}
      onEdit={(c) => setEditing({ ...c })}
      onDelete={async (c) => {
        if (!window.confirm(`¿Eliminar "${c.name}"?`)) return;
        try { await api.delete(`/workshops-directory/${c.id}`); load(); }
        catch (e) { alert(e.response?.data?.detail || "Error"); }
      }}
      importType="workshops"
      onImported={load}
    >
      {editing && <CompanyModal value={editing} type="workshop" onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </Section>
  );
}

function Section({ title, description, items, newLabel, onNew, onEdit, onDelete, importType, onImported, children }) {
  const [importOpen, setImportOpen] = useState(false);
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <p className="veta-tag">{description}</p>
          <h3 className="font-display text-2xl mt-1">{title} · {items.length}</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setImportOpen(true)} className="h-12 px-4 border-2 border-black text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors flex items-center gap-2" data-testid="import-btn">
            <Upload size={14} strokeWidth={2.5} /> Importar Excel
          </button>
          <button onClick={onNew} className="h-12 px-4 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 transition-colors flex items-center gap-2" data-testid="new-company-btn">
            <Plus size={14} strokeWidth={2.5} /> {newLabel}
          </button>
        </div>
      </div>

      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr><Th>Nombre</Th><Th>RUT</Th><Th>Contacto</Th><Th>Dirección</Th><Th></Th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-gray-200" data-testid={`company-row-${c.id}`}>
                <td className="px-4 py-3">
                  <p className="font-bold text-sm">{c.name}</p>
                  {c.is_directory && <p className="text-xs text-orange-600 font-bold uppercase tracking-wider">Sin usuarios aún</p>}
                </td>
                <td className="px-4 py-3 text-sm font-mono">{c.rut || "—"}</td>
                <td className="px-4 py-3 text-xs font-mono">
                  <p>{c.email || "—"}</p>
                  <p className="text-gray-500">{c.phone || ""}</p>
                </td>
                <td className="px-4 py-3 text-xs">{c.address || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => onEdit(c)} className="text-xs font-bold uppercase tracking-wider text-blue-600 underline mr-3" data-testid={`edit-${c.id}`}>
                    <Pencil size={12} strokeWidth={2.5} className="inline mr-1" /> Editar
                  </button>
                  <button onClick={() => onDelete(c)} className="text-xs font-bold uppercase tracking-wider text-red-600 underline" data-testid={`delete-${c.id}`}>
                    <Trash2 size={12} strokeWidth={2.5} className="inline mr-1" /> Eliminar
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">Sin registros aún. Crea uno o importa desde Excel.</td></tr>}
          </tbody>
        </table>
      </div>

      {importOpen && <ImportModal type={importType} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); onImported(); }} />}
      {children}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{children}</th>;
}

function CompanyModal({ value, type, onClose, onDone }) {
  const [form, setForm] = useState(value);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isNew = !!value._new;
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.rut && !validateRut(form.rut)) {
      setError(`RUT inválido: ${form.rut}`);
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, rut: form.rut ? formatRut(form.rut) : "" };
      delete payload._new;
      delete payload.id;
      if (isNew) {
        if (type === "client") {
          await api.post("/client-companies/quick-create", { name: payload.name, rut: payload.rut });
          // patch with rest of fields
          // (quick-create only takes name+rut; if more fields, do PUT after)
          // actually let's grab the new entity and update
          const list = await api.get("/client-companies");
          const fresh = list.data.find((x) => x.name === payload.name);
          if (fresh && (payload.address || payload.phone || payload.email)) {
            await api.put(`/clients/${fresh.id}`, payload);
          }
        } else {
          await api.post("/workshops-directory", payload);
        }
      } else {
        const path = type === "client" ? `/clients/${form.id}` : `/workshops-directory/${form.id}`;
        await api.put(path, payload);
      }
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || "Error");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6">
      <div className="bg-white border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-modal">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl">{isNew ? "Nueva empresa" : "Editar empresa"}</h2>
          <button onClick={onClose}><X size={24} strokeWidth={2.5} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Nombre / Razón Social" required value={form.name} onChange={update("name")} testId="comp-name" />
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="RUT (opcional)" value={form.rut} onChange={update("rut")} mono placeholder="76.123.456-0" testId="comp-rut" />
            <Field label="Teléfono" value={form.phone} onChange={update("phone")} mono testId="comp-phone" />
          </div>
          <Field label="Correo" type="email" value={form.email} onChange={update("email")} mono testId="comp-email" />
          <Field label="Dirección" value={form.address} onChange={update("address")} testId="comp-address" />
          {error && <div className="status-danger px-4 py-3 text-sm">{error}</div>}
          <button type="submit" disabled={loading} className="w-full h-12 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60" data-testid="save-company">
            {loading ? "Guardando..." : isNew ? "Crear" : "Guardar cambios"}
          </button>
        </form>
      </div>
    </div>
  );
}

const TYPE_LABEL = {
  vehicles: "Vehículos",
  clients: "Empresas Cliente",
  workshops: "Talleres",
  rentacars: "Rent-a-Cars",
};

const TYPE_FILE_SUFFIX = {
  vehicles: "vehiculos",
  clients: "empresas-cliente",
  workshops: "talleres",
  rentacars: "rentacars",
};

export function ImportModal({ type, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const label = TYPE_LABEL[type] || type;

  // Close on Escape
  useEffect(() => {
    const onKey = (ev) => { if (ev.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const downloadTemplate = async () => {
    setDownloading(true);
    setError("");
    try {
      const r = await api.get(`/import/template/${type}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla-${TYPE_FILE_SUFFIX[type] || type}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || "No se pudo descargar la plantilla");
    }
    setDownloading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setResult(null); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/import/${type}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Ocurrió un error al procesar el archivo");
    }
    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center md:p-6"
      onClick={onClose}
      data-testid="import-modal-backdrop"
    >
      <div
        className="bg-white border-4 border-black w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="import-modal"
      >
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="font-display text-2xl">Importar Excel · {label}</h2>
          <button onClick={onClose} aria-label="Cerrar" data-testid="import-close-btn"><X size={24} strokeWidth={2.5} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="border border-gray-200 p-4 bg-gray-50">
            <p className="veta-tag mb-2">Paso 1 · Descargar plantilla</p>
            <p className="text-xs text-gray-600 mb-3">
              Descarga la plantilla, completa tus datos <b>reemplazando las filas de ejemplo</b> y vuelve a subirla. La primera fila (encabezado) no debe modificarse.
            </p>
            <div className="border-l-4 border-yellow-500 bg-yellow-50 p-3 mb-3 text-xs text-yellow-900">
              <b>⚠ Importante:</b> las filas de ejemplo que trae la plantilla también se importan. Bórralas o reemplázalas con tus datos reales antes de subir el archivo.
            </div>
            <button
              type="button"
              onClick={downloadTemplate}
              disabled={downloading}
              className="inline-flex items-center gap-2 h-10 px-4 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 disabled:opacity-60"
              data-testid={`download-template-${type}`}
            >
              <Download size={14} strokeWidth={2.5} /> {downloading ? "Descargando..." : "Descargar plantilla XLSX"}
            </button>
          </div>

          {!result && (
            <form onSubmit={submit} className="space-y-3">
              <p className="veta-tag">Paso 2 · Subir archivo</p>
              <label className="block">
                <span className="sr-only">Selecciona archivo Excel</span>
                <input
                  type="file"
                  accept=".xlsx"
                  required
                  onChange={(e) => setFile(e.target.files?.[0])}
                  className="block w-full text-sm text-gray-700 file:mr-4 file:h-10 file:px-4 file:border-0 file:bg-black file:text-white file:text-xs file:font-bold file:uppercase file:tracking-wider file:cursor-pointer hover:file:bg-blue-600"
                  data-testid="import-file-input"
                />
              </label>
              {file && (
                <p className="text-xs text-gray-600 font-mono">Archivo seleccionado: <b>{file.name}</b></p>
              )}
              {error && <div className="status-danger p-3 text-sm" data-testid="import-error">{error}</div>}
              <button type="submit" disabled={!file || loading} className="w-full h-12 bg-blue-600 text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-700 disabled:opacity-50" data-testid="import-submit">
                {loading ? "Procesando..." : "Procesar archivo"}
              </button>
            </form>
          )}

          {result && (
            <div className="space-y-4" data-testid="import-result">
              <div className="grid grid-cols-3 gap-px bg-gray-200 border border-gray-200">
                <div className="bg-white p-3 text-center"><p className="veta-tag">Filas</p><p className="font-display text-2xl">{result.total_rows}</p></div>
                <div className="bg-green-50 p-3 text-center"><p className="veta-tag text-green-700">Creadas</p><p className="font-display text-2xl text-green-700">{result.created_count}</p></div>
                <div className="bg-red-50 p-3 text-center"><p className="veta-tag text-red-700">Errores</p><p className="font-display text-2xl text-red-700">{result.error_count}</p></div>
              </div>
              {result.errors.length > 0 && (
                <div className="border border-red-300">
                  <p className="bg-red-50 p-3 veta-tag text-red-700">Errores reportados</p>
                  <div className="max-h-48 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <div key={i} className="p-3 border-b border-gray-200 text-xs flex gap-3">
                        <span className="font-mono font-bold">Fila {e.row}:</span>
                        <span className="text-red-700">{e.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={onDone} className="w-full h-12 bg-black text-white text-xs font-bold uppercase tracking-wider" data-testid="import-close">Cerrar</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
